import {
  Address,
  AddressDetailDto,
  FORM_ID,
  ScrollableAlert,
  SelectedAddress,
  useAddressDetail,
  useTranslatedOptionEnum,
} from '@applyonline/shared'
import { AddressAutoCompleteInput } from '@applyonline/shared/components'
import { calculateTotalMonths, helpTip, setYearMonthValueFromMonths, useFormAutoSave } from '@applyonline/shared/utils'
import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingProgram, EProvince, getEProvinceList } from '@if/core/types'
import { formatAddress } from '@if/core/utils'
import { range } from 'lodash-es'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { getRecaptchaToken } from '../../../services/recaptcha'
import { HomeInformationInput, HomeInformationInputSchema } from '../../../types/Schemas/CreditApplicationSchema'
import HousingSection from './HousingInformations'

declare global {
  interface Window {
    initAutocomplete: () => void
  }
}

interface Props {
  applicantName: string
  applicantData: HomeInformationInput
  forceProvince?: EProvince
  onApplicantChanged: (data: HomeInformationInput) => void
  nextStepAfter: (currentStep: string) => void
  currentStep: string
  setDisableNext: (disable: boolean) => void
  isSinglePayment?: boolean
  applicantBirthDate: string
}

const AddressForm = ({
  forceProvince,
  applicantData,
  onApplicantChanged,
  nextStepAfter,
  currentStep,
  applicantName,
  setDisableNext,
  isSinglePayment,
  applicantBirthDate,
}: Props) => {
  const {
    t,
    i18n: { resolvedLanguage: lang },
  } = useTranslation()
  const yearSelectRef = useRef<HTMLSelectElement>(null)
  const monthSelectRef = useRef<HTMLSelectElement>(null)
  const [showAlert, setShowAlert] = useState(false)
  const [isEqualError, setIsEqualError] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState<SelectedAddress | null>(null)
  const [showAutoCompleteAddressInput, setShowAutoCompleteAddressInput] = useState(true)
  const [addressDetail] = useAddressDetail(EFinancingProgram.MechanicalWarranty)
  const { executeRecaptcha } = useGoogleReCaptcha()

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
    trigger,
    subscribe,
  } = useForm<HomeInformationInput>({
    mode: 'onBlur',
    resolver: yupResolver(HomeInformationInputSchema(isSinglePayment, applicantBirthDate)),
    defaultValues: applicantData,
  })

  useFormAutoSave(subscribe, onApplicantChanged)

  const onSubmit: SubmitHandler<HomeInformationInput> = (data) => {
    onApplicantChanged(data)
    nextStepAfter(currentStep)
  }

  useEffect(() => {
    if (applicantData.currentAddress.months > 0)
      setYearMonthValueFromMonths(
        applicantData.currentAddress.months,
        yearSelectRef.current!,
        monthSelectRef.current,
        true,
      )

    helpTip()
  }, [applicantData.currentAddress.months, trigger])

  const civicNumber = watch('currentAddress.civicNumber')
  const apartmentNumber = watch('currentAddress.apartment')

  const onChangeMonth = () => {
    const year = +(yearSelectRef.current?.value ?? 0)
    const month = +(monthSelectRef.current?.value ?? 0)
    const newTotalMount = calculateTotalMonths(year, month, true)

    setValue('currentAddress.months', newTotalMount, { shouldValidate: true, shouldTouch: true, shouldDirty: true })

    if (newTotalMount > 0) clearErrors('currentAddress.months')
  }

  const equalNumbers = useMemo(() => {
    return (
      civicNumber === apartmentNumber &&
      civicNumber !== '' &&
      apartmentNumber !== '' &&
      civicNumber !== undefined &&
      apartmentNumber !== undefined
    )
  }, [civicNumber, apartmentNumber])

  useEffect(() => {
    setDisableNext(equalNumbers)
    setShowAlert(true)
    setIsEqualError(equalNumbers)
  }, [equalNumbers, setDisableNext])

  const handleClearFields = () => {
    setShowAlert(false)
  }

  const onChangeAddress = async (address: SelectedAddress) => {
    if (!address) return
    const recaptchaToken = await getRecaptchaToken(executeRecaptcha, 'submit_addressDetail')
    const dto: AddressDetailDto = {
      placeId: address.placeId,
      recaptchaToken,
      userLanguageCode: lang!,
    }

    const ret = await addressDetail(dto)

    const currentAddress = {
      civicNumber: ret.address.civicNumber,
      street: ret.address.street,
      apartment: ret.address.apartment ?? '',
      postalCode: ret.address.postalCode,
      city: ret.address.city,
      stateIso: ret.address.stateIso,
      country: ret.address.country,
    }

    Object.entries(currentAddress).forEach(([key, value]) => {
      setValue(`currentAddress.${key as keyof Address}`, value ?? '', {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      })
    })
  }

  const hasAddressAutoCompleteInputError = () => {
    if (!errors.currentAddress) return false

    const fieldsToCheck: (keyof typeof errors.currentAddress)[] = [
      'civicNumber',
      'street',
      'postalCode',
      'city',
      'stateIso',
    ]

    return fieldsToCheck.some((field) => errors.currentAddress?.[field])
  }
  const addressError = errors.currentAddress?.stateIso?.message

  const provinceOptions = useTranslatedOptionEnum('EProvince', getEProvinceList(), 'enum')

  return (
    <section className="step-content">
      <h3 className="form-question">{t('address.provideAddress', { firstName: applicantName })}</h3>
      <ScrollableAlert
        type="error"
        showAlert={equalNumbers && showAlert}
        message={t('common.sameCivicAndAppartmentNumber')}
        buttons={[
          {
            id: 'btn-dismiss',
            label: t('common.yes'),
            onClick: () => {
              setShowAlert(false)
              setDisableNext(false)
              setIsEqualError(false)
            },
          },
          { id: 'btn-correct', label: t('common.modify'), onClick: () => handleClearFields() },
        ]}
      />
      {addressError && (
        <ScrollableAlert
          type="error"
          showAlert={addressError !== undefined && addressError !== ''}
          message={addressError}
        />
      )}
      <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID} noValidate>
        {showAutoCompleteAddressInput ? (
          <>
            <ScrollableAlert
              type="error"
              showAlert={!!errors.currentAddress?.country || hasAddressAutoCompleteInputError()}
              message={
                errors.currentAddress?.country
                  ? t('addressAutoComplete.canadaAddress')
                  : t('addressAutoComplete.refineSearch')
              }
            />
            <AddressAutoCompleteInput
              setShowAutoCompleteAddressInput={setShowAutoCompleteAddressInput}
              selectedAddress={selectedAddress}
              setSelectedAddress={setSelectedAddress}
              onChangeAddress={onChangeAddress}
              fullAddressDisplay={
                applicantData.currentAddress.civicNumber ? formatAddress(applicantData.currentAddress) : ''
              }
              financingProgramId={EFinancingProgram.MechanicalWarranty}
              getRecaptchaToken={getRecaptchaToken}
            />
          </>
        ) : (
          <>
            <div
              className={`control-group mid ${errors.currentAddress?.civicNumber ? 'error' : ''} ${isEqualError ? 'error' : ''}`}
            >
              <label htmlFor="civic_number">{t('address.addressNumber')}</label>
              <input
                type="text"
                inputMode="numeric"
                id="civicNumber"
                autoComplete="address-line1"
                {...register('currentAddress.civicNumber')}
                maxLength={10}
              />
            </div>

            <div className={`control-group mid ${errors.currentAddress?.street && 'error'}`}>
              <label htmlFor="street_name">{t('address.streetName')}</label>
              <input type="text" id="street" {...register('currentAddress.street')} maxLength={100} />
            </div>

            <div className={`control-group mid ${errors.currentAddress?.city && 'error'}`}>
              <label htmlFor="city">{t('address.city')}</label>
              <input type="text" id="city" {...register('currentAddress.city')} maxLength={50} />
            </div>

            <div className="control-group mid">
              <label htmlFor="select">{t('address.province')}</label>
              <div className={`dropdown-wrap ${errors.currentAddress?.stateIso && 'error'}`}>
                <select id="stateIso" {...register('currentAddress.stateIso')} defaultValue="">
                  {!forceProvince && (
                    <>
                      <option value="" disabled>
                        {' '}
                      </option>
                      {provinceOptions}
                    </>
                  )}
                  {forceProvince && (
                    <option value={forceProvince}>{t(`EProvince.${forceProvince}`, { ns: 'enum' })}</option>
                  )}
                </select>
              </div>
            </div>

            <div className={`control-group mid ${errors.currentAddress?.postalCode && 'error'}`}>
              <label htmlFor="postal_code">{t('address.postalCode')}</label>
              <input type="text" id="postalCode" {...register('currentAddress.postalCode')} maxLength={7} />
            </div>
          </>
        )}

        <div
          className={`control-group mid ${errors.currentAddress?.apartment ? 'error' : ''} ${isEqualError ? 'error' : ''}`}
        >
          <label htmlFor="apartment_number">{t('address.apartmentNumber')}</label>
          <input type="text" id="apartment" {...register('currentAddress.apartment')} maxLength={50} />
        </div>

        <div className="control-group" style={{ paddingBottom: 0 }}>
          <label htmlFor="year" style={{ marginBottom: 0 }}>
            {t('livingAtAddressSince.howLong')}
            <span className="help-tip">
              <p>{t('livingAtAddressSince.details')}</p>
            </span>
          </label>
        </div>

        <div className="control-group mid">
          <div className={`dropdown-wrap ${errors.currentAddress?.months ? 'error' : ''}`}>
            <select id="year" name="year" onChange={onChangeMonth} ref={yearSelectRef} defaultValue={0}>
              <option value={0}>{t('common.years')}</option>
              <option value={-1}>{t('common.lessThanOneYear')}</option>
              {range(1, 15).map((item: number) => (
                <option value={item} key={item}>
                  {t('common.yearsLabel', { count: item })}
                </option>
              ))}
              <option value={15}>
                {t('common.moreThan')} {t('common.yearsLabel', { count: 15 })}
              </option>
            </select>
          </div>
        </div>

        {!isSinglePayment && applicantData.spending != null && (
          <HousingSection
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            control={control}
            spending={applicantData.spending}
          />
        )}
      </form>
    </section>
  )
}

export default AddressForm
